import React from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

// Main layout wrapper with responsive padding and full height
export const PageLayout = ({ children, maxWidth = false, sx = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        py: { xs: 0.5, sm: 1, md: 1 },
        px: { xs: 0, sm: 0.5, md: 0.5 },
        pb: { xs: 1.5, sm: 2 },
        maxWidth: maxWidth || "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Section wrapper with consistent spacing
export const Section = ({ children, spacing = 1, sx = {} }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mb: spacing,
        "&:last-child": {
          mb: 0,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Responsive grid container
export const GridContainer = ({
  children,
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
  spacing = 3,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: `repeat(${columns.xs}, 1fr)`,
          sm: `repeat(${columns.sm}, 1fr)`,
          md: `repeat(${columns.md}, 1fr)`,
          lg: `repeat(${columns.lg}, 1fr)`,
        },
        gap: spacing,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Flex container with responsive direction
export const FlexContainer = ({
  children,
  direction = { xs: "column", sm: "row" },
  justifyContent = "flex-start",
  alignItems = "stretch",
  spacing = 2,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction,
        justifyContent,
        alignItems,
        gap: spacing,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// Card wrapper with consistent styling
export const CardWrapper = ({ children, elevation = 1, sx = {} }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        // height: "55px",
        backgroundColor: "transparent",
        borderRadius: theme.shape.borderRadius * 1.33,
        // boxShadow: theme.shadows[elevation],
        p: { xs: 1, sm: 1 },
        ...sx,
        mb: 0,
        ml: 1,
        pl: 1,
      }}
    >
      {children}
    </Box>
  );
};

// Responsive header component
export const PageHeader = ({ title, subtitle, action, sx = {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: 2,
        pt: 0,
        mt: 0,
        mb: 0.5,
        ...sx,
      }}
    >
      <Box>
        <Box
          component="h1"
          sx={{
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: subtitle ? 0.5 : 0,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Box>
        {subtitle && (
          <Box
            component="p"
            sx={{
              fontSize: { xs: "0.85rem", sm: ".75rem" },
              color: theme.palette.text.secondary,
              m: 0,
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Box>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
};

export default {
  PageLayout,
  Section,
  GridContainer,
  FlexContainer,
  CardWrapper,
  PageHeader,
};
